/**
 * Feature Flag Service
 *
 * - `resolve()` is PUBLIC (no auth) and MUST send cookies so the backend can
 *   match OPT_IN tokens stored in `ff_<key>` cookies. We use the `api` axios
 *   instance with `withCredentials: true` per-call. The `Authorization`
 *   header is added by the interceptor only when a token exists, and the
 *   backend `@Public()` route ignores it.
 *
 * - All master CRUD calls use the authenticated `api` client. They require a
 *   valid MASTER token (enforced server-side by `@MasterOnly()`).
 */
import { api } from './api';

export type FeatureFlagStatus = 'ON' | 'OFF' | 'OPT_IN';

export interface ResolvedFlag {
  key: string;
  /**
   * Raw flag status (ATR-307). Optional on purpose: a stale/partial payload
   * without it must fail SAFE (treated as enabled by `useAiPlatformEnabled`),
   * and it keeps existing `{ key, enabled }` mocks valid. The live backend
   * always sends it now.
   */
  status?: FeatureFlagStatus;
  enabled: boolean;
}

interface ResolveResponse {
  flags: ResolvedFlag[];
}

export interface FeatureFlagDto {
  id: string;
  key: string;
  description: string | null;
  status: FeatureFlagStatus;
  optInToken: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeatureFlagInput {
  key: string;
  description?: string;
  status: FeatureFlagStatus;
}

export interface UpdateFeatureFlagInput {
  description?: string;
  status?: FeatureFlagStatus;
}

const MASTER_BASE = '/system/feature-flags';

export const featureFlagService = {
  async resolve(): Promise<ResolvedFlag[]> {
    const { data } = await api.get<ResolveResponse>('/feature-flags/resolve', {
      withCredentials: true,
    });
    return data?.flags ?? [];
  },

  async list(): Promise<FeatureFlagDto[]> {
    const { data } = await api.get<FeatureFlagDto[]>(MASTER_BASE);
    return data;
  },

  async create(input: CreateFeatureFlagInput): Promise<FeatureFlagDto> {
    const { data } = await api.post<FeatureFlagDto>(MASTER_BASE, input);
    return data;
  },

  async update(id: string, input: UpdateFeatureFlagInput): Promise<FeatureFlagDto> {
    const { data } = await api.patch<FeatureFlagDto>(`${MASTER_BASE}/${id}`, input);
    return data;
  },

  async rotateToken(id: string): Promise<FeatureFlagDto> {
    const { data } = await api.post<FeatureFlagDto>(`${MASTER_BASE}/${id}/rotate-token`);
    return data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`${MASTER_BASE}/${id}`);
  },
};
